import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  fetchAvailableAccessCoupons,
  fetchCouponSends,
  fetchRecipientCounts,
  sendAccessCoupons,
  sendRegularCoupon,
} from "../utils/affiliateApi";

export const AVAILABLE_ACCESS_COUPONS_QUERY_KEY = ["affiliate", "access-coupons", "available"];
export const COUPON_SENDS_QUERY_KEY = ["affiliate", "coupon-sends"];
export const RECIPIENT_COUNTS_QUERY_KEY = ["affiliate", "coupon-sends", "recipient-counts"];

/** Access coupons still available to send (not redeemed, not already sent). */
export const useAvailableAccessCoupons = ({ enabled = true } = {}) => {
  return useQuery({
    queryKey: AVAILABLE_ACCESS_COUPONS_QUERY_KEY,
    queryFn: () => fetchAvailableAccessCoupons(),
    enabled,
    refetchOnWindowFocus: false,
  });
};

/** Sent-coupon history: `kind` is "access" | "regular" | "all". */
export const useCouponSends = ({ kind = "access", limit = 50, offset = 0, enabled = true } = {}) => {
  return useQuery({
    queryKey: [...COUPON_SENDS_QUERY_KEY, { kind, limit, offset }],
    queryFn: () => fetchCouponSends({ kind, limit, offset }),
    enabled,
    refetchOnWindowFocus: false,
  });
};

/**
 * Lifetime access-coupon counts for the given addresses, powering the
 * "this address already received one" warning. The key is the sorted address
 * list so adding a recipient reuses the cache for the rest.
 */
export const useRecipientAccessCounts = (emails = [], { enabled = true } = {}) => {
  const normalized = Array.from(new Set(emails.map((e) => String(e).trim().toLowerCase()).filter(Boolean))).sort();
  return useQuery({
    queryKey: [...RECIPIENT_COUNTS_QUERY_KEY, normalized],
    queryFn: () => fetchRecipientCounts(normalized),
    enabled: enabled && normalized.length > 0,
    refetchOnWindowFocus: false,
  });
};

/**
 * Bulk access-coupon send. Takes the recipients array
 * (`[{ email, coupon_ids }]`) and refreshes the pool, the history and the
 * per-recipient counts once the batch comes back.
 */
export const useSendAccessCoupons = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recipients) => sendAccessCoupons(recipients),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: AVAILABLE_ACCESS_COUPONS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: COUPON_SENDS_QUERY_KEY });
      queryClient.invalidateQueries({ queryKey: RECIPIENT_COUNTS_QUERY_KEY });
    },
  });
};

/** Single regular (non-access) coupon send. */
export const useSendRegularCoupon = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ email, couponId }) => sendRegularCoupon(email, couponId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: COUPON_SENDS_QUERY_KEY });
    },
  });
};
